import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../utils/api';
import { fmt, unitLabel } from '../utils/format';
import Alert from '../components/Alert';
import Pager from '../components/Pager';

const UNITS = ['kg', 'l'];

const EMPTY_FORM = {
  product_name: '',
  sku: '',
  mrp: '',
  cases: '',
  pcs: '',
  quantity: '',
  unit: '',
  gst_percent: '',
  purchase_date: new Date().toISOString().slice(0, 10),
  amount: '',
};

export default function Purchases() {
  const [purchases, setPurchases] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);

  const [filterProduct, setFilterProduct] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({});

  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // null = new, object = edit
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  const loadPurchases = useCallback(async (filters, pg) => {
    setLoading(true);
    try {
      const res = await api.get('/purchases', { params: { ...filters, page: pg } });
      setPurchases(res.data.purchases);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch {
      setAlert({ type: 'danger', msg: 'Could not load purchases.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPurchases(appliedFilters, page);
  }, [loadPurchases, appliedFilters, page]);

  function applyFilters() {
    const filters = {};
    if (filterProduct.trim()) filters.product = filterProduct.trim();
    if (filterDateFrom) filters.dateFrom = filterDateFrom;
    if (filterDateTo) filters.dateTo = filterDateTo;
    setAppliedFilters(filters);
    setPage(1);
  }

  function clearFilters() {
    setFilterProduct('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setAppliedFilters({});
    setPage(1);
  }

  function openAdd() {
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setModalOpen(true);
  }

  function openEdit(purchase) {
    setEditTarget(purchase);
    setForm({
      product_name: purchase.product_name || '',
      sku: purchase.sku || '',
      mrp: purchase.mrp != null ? String(purchase.mrp) : '',
      cases: purchase.cases != null ? String(purchase.cases) : '',
      pcs: purchase.pcs != null ? String(purchase.pcs) : '',
      quantity: purchase.quantity != null ? String(purchase.quantity) : '',
      unit: purchase.unit || '',
      gst_percent: purchase.gst_percent != null ? String(purchase.gst_percent) : '',
      purchase_date: purchase.purchase_date ? String(purchase.purchase_date).slice(0, 10) : new Date().toISOString().slice(0, 10),
      amount: purchase.amount != null ? String(purchase.amount) : '',
    });
    setFormError('');
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setEditTarget(null);
  }

  async function handleSave() {
    if (!form.product_name.trim()) { setFormError('Product name is required.'); return; }
    setSaving(true);
    setFormError('');
    try {
      if (editTarget) {
        const res = await api.put(`/purchases/${editTarget.id}`, form);
        setPurchases((prev) => prev.map((p) => (p.id === res.data.purchase.id ? res.data.purchase : p)));
        setAlert({ type: 'success', msg: 'Purchase updated.' });
      } else {
        await api.post('/purchases', form);
        setAlert({ type: 'success', msg: 'Purchase added.' });
        loadPurchases(appliedFilters, 1);
        setPage(1);
      }
      closeModal();
    } catch (err) {
      setFormError(err?.response?.data?.error || 'Could not save purchase.');
    } finally {
      setSaving(false);
    }
  }

  async function handleExport() {
    setExporting(true);
    try {
      const res = await api.get('/purchases/export', {
        params: appliedFilters,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'purchases.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setAlert({ type: 'danger', msg: 'Export failed.' });
    } finally {
      setExporting(false);
    }
  }

  function field(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  const hasFilters = filterProduct || filterDateFrom || filterDateTo;

  return (
    <Layout title="Purchases">
      <Alert type={alert?.type} message={alert?.msg} onClose={() => setAlert(null)} />

      {/* Filters row */}
      <div style={{ display: 'flex', gap: 8, marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          style={{ maxWidth: 200 }}
          placeholder="Product name…"
          value={filterProduct}
          onChange={(e) => setFilterProduct(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
        />
        <input
          type="date"
          style={{ maxWidth: 150 }}
          value={filterDateFrom}
          onChange={(e) => setFilterDateFrom(e.target.value)}
          title="Date from"
        />
        <input
          type="date"
          style={{ maxWidth: 150 }}
          value={filterDateTo}
          onChange={(e) => setFilterDateTo(e.target.value)}
          title="Date to"
        />
        <button className="btn btn-primary" onClick={applyFilters}>
          <i className="ti ti-search" aria-hidden="true"></i>Search
        </button>
        {hasFilters && (
          <button className="btn" onClick={clearFilters}>
            <i className="ti ti-x" aria-hidden="true"></i>Clear
          </button>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button className="btn" onClick={handleExport} disabled={exporting}>
            <i className="ti ti-download" aria-hidden="true"></i>
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
          <button className="btn btn-primary" onClick={openAdd}>
            <i className="ti ti-plus" aria-hidden="true"></i>Add Purchase
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="empty-state">Loading purchases…</div>
        ) : purchases.length === 0 ? (
          <div className="empty-state">
            <i className="ti ti-shopping-cart" aria-hidden="true"></i>
            No purchases found
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Product Name</th>
                <th>SKU</th>
                <th>MRP</th>
                <th>Date</th>
                <th>Cases</th>
                <th>PCS</th>
                <th>Qty</th>
                <th>Unit</th>
                <th>GST%</th>
                <th>Amount</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {purchases.map((p, i) => (
                <tr key={p.id}>
                  <td>{(page - 1) * 20 + i + 1}</td>
                  <td style={{ fontWeight: 600, fontSize: 11 }}>{p.product_name}</td>
                  <td style={{ fontSize: 11, color: '#9e9e9e' }}>{p.sku || '—'}</td>
                  <td>{fmt(p.mrp)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{p.purchase_date ? new Date(p.purchase_date).toISOString().slice(0, 10) : '—'}</td>
                  <td>{p.cases}</td>
                  <td>{p.pcs}</td>
                  <td>{parseFloat(p.quantity || 0)}</td>
                  <td>{unitLabel(p.unit) || '—'}</td>
                  <td>{parseFloat(p.gst_percent || 0).toFixed(2)}%</td>
                  <td><strong>{fmt(p.amount)}</strong></td>
                  <td>
                    <button className="btn btn-sm btn-primary" onClick={() => openEdit(p)} title="Edit">
                      <i className="ti ti-edit" aria-hidden="true"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Pager page={page} pages={pages} total={total} onPage={setPage} label="purchases" />

      {/* Add / Edit Modal */}
      {modalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">{editTarget ? 'Edit Purchase' : 'Add Purchase'}</div>
              <button className="modal-close" onClick={closeModal}>&times;</button>
            </div>
            <div className="modal-body">
              <Alert type="danger" message={formError} onClose={() => setFormError('')} />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">Product Name <span style={{ color: 'var(--danger)' }}>*</span></label>
                  <input value={form.product_name} onChange={(e) => field('product_name', e.target.value)} placeholder="Product name" />
                </div>

                <div className="form-group">
                  <label className="form-label">SKU</label>
                  <input value={form.sku} onChange={(e) => field('sku', e.target.value)} placeholder="SKU" />
                </div>

                <div className="form-group">
                  <label className="form-label">MRP</label>
                  <input type="number" min="0" step="0.01" value={form.mrp} onChange={(e) => field('mrp', e.target.value)} placeholder="0.00" />
                </div>

                <div className="form-group">
                  <label className="form-label">Cases</label>
                  <input type="number" min="0" value={form.cases} onChange={(e) => field('cases', e.target.value)} placeholder="0" />
                </div>

                <div className="form-group">
                  <label className="form-label">PCS</label>
                  <input type="number" min="0" value={form.pcs} onChange={(e) => field('pcs', e.target.value)} placeholder="0" />
                </div>

                <div className="form-group">
                  <label className="form-label">Quantity</label>
                  <input
                    type="number"
                    min="0"
                    step={form.unit ? '0.001' : '1'}
                    value={form.quantity}
                    onChange={(e) => {
                      const v = e.target.value;
                      field('quantity', form.unit ? v : v.replace(/\.\d*/, ''));
                    }}
                    placeholder="0"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Unit</label>
                  <select
                    value={form.unit}
                    onChange={(e) => {
                      const newUnit = e.target.value;
                      setForm((f) => ({
                        ...f,
                        unit: newUnit,
                        quantity: !newUnit && f.quantity ? String(parseInt(parseFloat(f.quantity) || 0)) : f.quantity,
                      }));
                    }}
                  >
                    <option value="">— None (packaged) —</option>
                    {UNITS.map((u) => <option key={u} value={u}>{unitLabel(u)}</option>)}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">GST%</label>
                  <input type="number" min="0" step="0.01" value={form.gst_percent} onChange={(e) => field('gst_percent', e.target.value)} placeholder="0" />
                </div>

                <div className="form-group">
                  <label className="form-label">Date</label>
                  <input type="date" value={form.purchase_date} onChange={(e) => field('purchase_date', e.target.value)} />
                </div>

                <div className="form-group">
                  <label className="form-label">Amount</label>
                  <input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => field('amount', e.target.value)} placeholder="0.00" />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={closeModal} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : editTarget ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
